import type {
  EquityResearchRecord,
  ScoreDimension,
} from "./equityResearchTypes";

export function getScoreDimensions(record: EquityResearchRecord): {
  dimensions: ScoreDimension[];
  average: number;
} {
  const dimensions: ScoreDimension[] = [
    {
      key: "niche",
      label: "Niche Strength",
      shortLabel: "Niche",
      score: Number(record.niche_score) || 0,
    },
    {
      key: "segment",
      label: "Segment Health",
      shortLabel: "Segments",
      score: Number(record.segment_health_score) || 0,
    },
    {
      key: "moat",
      label: "Economic Moat",
      shortLabel: "Moat",
      score: Number(record.moat_strength_score) || 0,
    },
    {
      key: "competition",
      label: "Competitiveness",
      shortLabel: "Compete",
      score: Number(record.competitiveness_score) || 0,
    },
    {
      key: "demand",
      label: "Demand Runway",
      shortLabel: "Demand",
      score: Number(record.demand_score) || 0,
    },
    {
      key: "risk",
      label: "Risk / Reward",
      shortLabel: "Risk",
      score: Number(record.risk_reward_score) || 0,
    },
    {
      key: "macro",
      label: "Macro Resilience",
      shortLabel: "Macro",
      score: Number(record.macro_resilience_score) || 0,
    },
  ];

  const average =
    dimensions.reduce((sum, d) => sum + d.score, 0) / dimensions.length || 0;

  return { dimensions, average };
}

/** Splits on commas that start the next "Label: value" entry, not commas inside values. */
export function parseFactorList(
  factors: string,
): { label: string; value: string }[] {
  if (!factors?.trim()) return [];

  let parts: string[];
  if (factors.includes("\n")) {
    parts = factors.split("\n");
  } else {
    // If no newlines, check if it's a comma-separated list of multiple factors
    const colonCount = (factors.match(/:/g) || []).length;
    if (colonCount > 1) {
      parts = factors.split(",");
    } else {
      parts = [factors];
    }
  }

  return parts
    .map((part) => part.replace(/^[•\-\d]+\.?\s*/, "").trim())
    .filter(Boolean)
    .map((part) => {
      const colonIdx = part.indexOf(":");
      if (colonIdx === -1) return { label: part, value: "" };
      return {
        label: part.slice(0, colonIdx).trim(),
        value: part.slice(colonIdx + 1).trim(),
      };
    });
}

export function getScoreColor(score: number): string {
  if (score >= 7) return "#00A85F";
  if (score >= 5) return "#f59e0b";
  return "#ef4444";
}

export function getScoreLabel(score: number): string {
  if (score >= 8) return "Excellent";
  if (score >= 7) return "Strong";
  if (score >= 5) return "Average";
  if (score >= 3) return "Weak";
  return "Poor";
}
